import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, apiGet } from '../../lib/api';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { cn } from '../../lib/cn';

type Tab = 'sales' | 'products' | 'general';
type Group = 'shop' | 'salesperson' | 'shop_salesperson' | 'category' | 'method';
type ProductGroup = 'model' | 'product' | 'category';
type GeneralGroup = 'date' | 'shop' | 'salesperson' | 'shop_salesperson';
type Rank = 'revenue' | 'qty';

interface TrendMeta {
  unit: 'day' | 'week' | 'month';
  labels: string[];
}
interface ReportPeriod {
  from: string;
  to: string;
  label: string;
}

interface ReportRow {
  key: string;
  label: string;
  shop?: { code: string; name: string };
  salesperson?: { name: string };
  orderCount: number;
  gross: number;
  discount: number;
  net: number;
  vat: number;
  total: number;
  avgTicket: number;
  commission: number;
  sparkline: number[];
}
interface SalesReport {
  period: ReportPeriod;
  groupBy: Group;
  trend: TrendMeta;
  rows: ReportRow[];
  totals: {
    orderCount: number;
    gross: number;
    discount: number;
    net: number;
    vat: number;
    total: number;
    commission: number;
  };
}

interface ProductRow {
  key: string;
  label: string;
  secondary: string | null;
  category: string | null;
  qty: number;
  returnedQty: number;
  orderCount: number;
  gross: number;
  discount: number;
  net: number;
  vat: number;
  total: number;
  avgPrice: number;
  share: number;
  sparkline: number[];
}
interface ProductReport {
  period: ReportPeriod;
  groupBy: ProductGroup;
  sort: Rank;
  trend: TrendMeta;
  rows: ProductRow[];
  totals: {
    qty: number;
    returnedQty: number;
    gross: number;
    discount: number;
    net: number;
    vat: number;
    total: number;
    orderCount: number;
    productCount: number;
  };
}

// General report — the director's per-sale template. The column set is decided
// by the backend (general-report.ts owns the template), so the preview table and
// the downloaded sheet can never drift apart.
interface GeneralColumn {
  header: string;
  key: string;
}
interface GeneralReport {
  period: ReportPeriod;
  groupBy: GeneralGroup;
  columns: GeneralColumn[];
  rows: Array<Record<string, string | number | null>>;
  rowCount: number;
  truncated: boolean;
  totals: {
    orders: number;
    rows: number;
    qty: number;
    amount: number;
    discount: number;
    collected: number;
    balanceDue: number;
    commission: number;
  };
}

interface ReportOptions {
  shops: Array<{ id: string; code: string; name: string }>;
  salespeople: Array<{ id: string; name: string }>;
  categories: Array<{ id: string; name: string }>;
}

// "All time" belongs to the general report only: the pivots draw a trend chart
// over the period, and a trend bucketed from the dawn of the calendar is noise.
const ALL_TIME = { key: 'all', label: 'All time' };
const PERIODS = [
  ALL_TIME,
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: '7 days' },
  { key: 'month', label: 'Month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'ytd', label: 'YTD' },
  { key: 'custom', label: 'Custom…' },
];
const GROUPS: { key: Group; label: string }[] = [
  { key: 'shop', label: 'Shop' },
  { key: 'shop_salesperson', label: 'Shop × Salesperson' },
  { key: 'salesperson', label: 'Salesperson' },
  { key: 'category', label: 'Category' },
  { key: 'method', label: 'Payment method' },
];
const PRODUCT_GROUPS: { key: ProductGroup; label: string }[] = [
  { key: 'model', label: 'Model' },
  { key: 'product', label: 'Variant (SKU)' },
  { key: 'category', label: 'Category' },
];
const RANKS: { key: Rank; label: string }[] = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'qty', label: 'Units sold' },
];
// "Group by" on a flat sheet is a sort — Excel's own grouping/subtotals then work
// off contiguous blocks. Date is the default (chronological, like the paper sheet).
const GENERAL_GROUPS: { key: GeneralGroup; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'shop', label: 'Shop' },
  { key: 'salesperson', label: 'Salesperson' },
  { key: 'shop_salesperson', label: 'Shop × Salesperson' },
];

interface SavedView {
  label: string;
  tab: Tab;
  period: string;
  group?: Group;
  productGroup?: ProductGroup;
  rank?: Rank;
}
const SAVED_VIEWS: SavedView[] = [
  { label: 'General report · all time', tab: 'general', period: 'all' },
  { label: 'General report · this month', tab: 'general', period: 'month' },
  { label: 'Owner monthly · by shop', tab: 'sales', period: 'month', group: 'shop' },
  { label: 'Best sellers · this month', tab: 'products', period: 'month', productGroup: 'model' },
  { label: 'Best sellers · last month', tab: 'products', period: 'last_month', productGroup: 'model' },
  { label: 'Top variants by units · month', tab: 'products', period: 'month', productGroup: 'product', rank: 'qty' },
  { label: 'Top sellers · this month', tab: 'sales', period: 'month', group: 'salesperson' },
  { label: 'Shop × person · MTD', tab: 'sales', period: 'month', group: 'shop_salesperson' },
  { label: 'Category mix · this month', tab: 'sales', period: 'month', group: 'category' },
  { label: 'Payment methods · 7 days', tab: 'sales', period: 'week', group: 'method' },
];

const VALID_GROUPS: Group[] = ['shop', 'salesperson', 'shop_salesperson', 'category', 'method'];
const VALID_PRODUCT_GROUPS: ProductGroup[] = ['model', 'product', 'category'];
const VALID_GENERAL_GROUPS: GeneralGroup[] = ['date', 'shop', 'salesperson', 'shop_salesperson'];
const VALID_PERIODS = PERIODS.map((p) => p.key);

function localYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const isYmd = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

const PCT = new Intl.NumberFormat('en-MU', { style: 'percent', maximumFractionDigits: 1 });

export function ReportsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const get = (k: string): string | null => searchParams.get(k);

  const initialTab: Tab =
    get('report') === 'products' ? 'products' : get('report') === 'general' ? 'general' : 'sales';
  const [tab, setTab] = useState<Tab>(initialTab);
  // The general report is the unfiltered sheet by default — all time, every
  // shop, everyone. The pivots default to the month, as they always have, and
  // never take "all time" even from a hand-edited URL.
  const urlPeriod = get('period');
  const [period, setPeriod] = useState(
    urlPeriod && VALID_PERIODS.includes(urlPeriod) && (initialTab === 'general' || urlPeriod !== 'all')
      ? urlPeriod
      : initialTab === 'general'
        ? 'all'
        : 'month',
  );
  const now = new Date();
  const [customFrom, setCustomFrom] = useState(
    get('from') && isYmd(get('from')!) ? get('from')! : localYmd(new Date(now.getFullYear(), now.getMonth(), 1)),
  );
  const [customTo, setCustomTo] = useState(get('to') && isYmd(get('to')!) ? get('to')! : localYmd(now));
  const [groupBy, setGroupBy] = useState<Group>(
    VALID_GROUPS.includes(get('groupBy') as Group) ? (get('groupBy') as Group) : 'shop_salesperson',
  );
  const [productGroupBy, setProductGroupBy] = useState<ProductGroup>(
    VALID_PRODUCT_GROUPS.includes(get('pgroup') as ProductGroup)
      ? (get('pgroup') as ProductGroup)
      : 'model',
  );
  const [generalGroupBy, setGeneralGroupBy] = useState<GeneralGroup>(
    VALID_GENERAL_GROUPS.includes(get('ggroup') as GeneralGroup)
      ? (get('ggroup') as GeneralGroup)
      : 'date',
  );
  const [rank, setRank] = useState<Rank>(get('rank') === 'qty' ? 'qty' : 'revenue');
  const [includeGifts, setIncludeGifts] = useState(get('gifts') === '1');
  const [shopId, setShopId] = useState(get('shopId') ?? '');
  const [salespersonId, setSalespersonId] = useState(get('salespersonId') ?? '');
  const [categoryId, setCategoryId] = useState(get('categoryId') ?? '');

  // Keep the URL shareable/bookmarkable — every filter lives in the query string.
  useEffect(() => {
    const p = new URLSearchParams();
    if (tab !== 'sales') p.set('report', tab);
    p.set('period', period);
    if (period === 'custom') {
      p.set('from', customFrom);
      p.set('to', customTo);
    }
    if (tab === 'sales') p.set('groupBy', groupBy);
    else if (tab === 'products') p.set('pgroup', productGroupBy);
    else if (generalGroupBy !== 'date') p.set('ggroup', generalGroupBy);
    if (tab === 'products' && rank !== 'revenue') p.set('rank', rank);
    if (tab === 'products' && includeGifts) p.set('gifts', '1');
    if (shopId) p.set('shopId', shopId);
    if (salespersonId) p.set('salespersonId', salespersonId);
    if (tab === 'products' && categoryId) p.set('categoryId', categoryId);
    setSearchParams(p, { replace: true });
  }, [tab, period, customFrom, customTo, groupBy, productGroupBy, generalGroupBy, rank, includeGifts, shopId, salespersonId, categoryId, setSearchParams]);

  const customValid =
    period !== 'custom' || (isYmd(customFrom) && isYmd(customTo) && customFrom <= customTo);

  const rangeParams: Record<string, string> = {
    period,
    ...(period === 'custom' ? { from: customFrom, to: customTo } : {}),
    ...(shopId ? { shopId } : {}),
    ...(salespersonId ? { salespersonId } : {}),
  };

  const options = useQuery({
    queryKey: ['report-options'],
    queryFn: () => apiGet<ReportOptions>('/finance/reports/options'),
    staleTime: 5 * 60 * 1000,
  });

  const salesParams = { ...rangeParams, groupBy };
  const salesReport = useQuery({
    queryKey: ['report-sales', salesParams],
    queryFn: () => apiGet<SalesReport>('/finance/reports/sales', salesParams),
    enabled: tab === 'sales' && customValid,
  });

  const productParams = {
    ...rangeParams,
    groupBy: productGroupBy,
    sort: rank,
    ...(categoryId ? { categoryId } : {}),
    ...(includeGifts ? { includeGifts: 'true' } : {}),
  };
  const productReport = useQuery({
    queryKey: ['report-products', productParams],
    queryFn: () => apiGet<ProductReport>('/finance/reports/products', productParams),
    enabled: tab === 'products' && customValid,
  });

  // groupBy is only sent when it isn't the default — the endpoint's "no filters
  // at all" default has to stay reachable by sending nothing.
  const generalParams = {
    ...rangeParams,
    ...(generalGroupBy !== 'date' ? { groupBy: generalGroupBy } : {}),
  };
  const generalReport = useQuery({
    queryKey: ['report-general', generalParams],
    queryFn: () => apiGet<GeneralReport>('/finance/reports/general', generalParams),
    enabled: tab === 'general' && customValid,
  });

  const applyView = (v: SavedView): void => {
    setTab(v.tab);
    setPeriod(v.period);
    if (v.group) setGroupBy(v.group);
    if (v.productGroup) setProductGroupBy(v.productGroup);
    setRank(v.rank ?? 'revenue');
    setShopId('');
    setSalespersonId('');
    setCategoryId('');
  };

  const [exporting, setExporting] = useState(false);
  const exportExcel = async (): Promise<void> => {
    const url =
      tab === 'sales'
        ? '/finance/reports/sales'
        : tab === 'products'
          ? '/finance/reports/products'
          : '/finance/reports/general';
    const params = tab === 'sales' ? salesParams : tab === 'products' ? productParams : generalParams;
    const filename =
      tab === 'general'
        ? `general-report-${period}.xlsx`
        : `${tab === 'sales' ? 'sales' : 'product'}-report-${tab === 'sales' ? groupBy : productGroupBy}-${period}.xlsx`;
    setExporting(true);
    try {
      const res = await api.get(url, { params: { ...params, format: 'excel' }, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 30_000);
    } finally {
      setExporting(false);
    }
  };

  const activeReport =
    tab === 'sales' ? salesReport.data : tab === 'products' ? productReport.data : generalReport.data;
  const periodLabel = activeReport?.period.label ?? PERIODS.find((p) => p.key === period)?.label;
  // The general report's preview is capped, so its own rowCount is the honest one.
  const rowCount =
    tab === 'general' ? generalReport.data?.rowCount : (activeReport as { rows: unknown[] } | undefined)?.rows.length;

  return (
    <PageScaffold
      title="Reports"
      subtitle={
        <>
          {tab === 'sales'
            ? `Sales · ${GROUPS.find((g) => g.key === groupBy)?.label}`
            : tab === 'products'
              ? `Products · ${PRODUCT_GROUPS.find((g) => g.key === productGroupBy)?.label}`
              : `General report · by ${GENERAL_GROUPS.find((g) => g.key === generalGroupBy)?.label}`}
          {periodLabel && ` · ${periodLabel}`}
          {rowCount != null && ` · ${rowCount} row${rowCount === 1 ? '' : 's'}`}
        </>
      }
      actions={
        <Button variant="secondary" onClick={exportExcel} disabled={exporting}>
          {exporting ? 'Preparing…' : 'Export Excel'}
        </Button>
      }
    >
      {/* Report switcher */}
      <div className="inline-flex rounded border border-rule bg-paper-2 p-0.5">
        {(
          [
            { key: 'sales', label: 'Sales performance' },
            { key: 'products', label: 'Product performance' },
            { key: 'general', label: 'General report' },
          ] as Array<{ key: Tab; label: string }>
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              // Switching INTO the general report resets it to its own default —
              // the whole book, unfiltered. Anything the user then picks sticks.
              // Leaving it drops "all time", which the pivots don't offer.
              if (t.key === 'general') setPeriod('all');
              else if (period === ALL_TIME.key) setPeriod('month');
            }}
            className={cn(
              'px-4 py-1.5 rounded text-sm transition',
              tab === t.key ? 'bg-surface text-ink shadow-sm border border-rule' : 'text-ink-3 hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* LEFT — filters + saved views */}
        <div className="space-y-4 lg:col-span-1">
          <Card title="Filters" bodyClassName="p-4 space-y-4">
            <FilterChips
              label="Period"
              options={tab === 'general' ? PERIODS : PERIODS.filter((p) => p.key !== ALL_TIME.key)}
              value={period}
              onChange={setPeriod}
            />
            {period === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">From</span>
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="kc-input text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">To</span>
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="kc-input text-sm"
                  />
                </label>
                {!customValid && (
                  <p className="col-span-2 text-xs text-negative">
                    Pick both dates — “from” must not be after “to”.
                  </p>
                )}
              </div>
            )}

            {tab === 'sales' ? (
              <FilterChips
                label="Group by"
                options={GROUPS}
                value={groupBy}
                onChange={(v) => setGroupBy(v as Group)}
              />
            ) : tab === 'general' ? (
              <FilterChips
                label="Group by"
                options={GENERAL_GROUPS}
                value={generalGroupBy}
                onChange={(v) => setGeneralGroupBy(v as GeneralGroup)}
              />
            ) : (
              <>
                <FilterChips
                  label="Group by"
                  options={PRODUCT_GROUPS}
                  value={productGroupBy}
                  onChange={(v) => setProductGroupBy(v as ProductGroup)}
                />
                <FilterChips
                  label="Rank by"
                  options={RANKS}
                  value={rank}
                  onChange={(v) => setRank(v as Rank)}
                />
              </>
            )}

            <FilterSelect label="Shop" value={shopId} onChange={setShopId}>
              <option value="">All shops</option>
              {(options.data?.shops ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect label="Salesperson" value={salespersonId} onChange={setSalespersonId}>
              <option value="">All salespeople</option>
              {(options.data?.salespeople ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </FilterSelect>
            {tab === 'products' && (
              <>
                <FilterSelect label="Category" value={categoryId} onChange={setCategoryId}>
                  <option value="">All categories</option>
                  {(options.data?.categories ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </FilterSelect>
                <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeGifts}
                    onChange={(e) => setIncludeGifts(e.target.checked)}
                    className="accent-[var(--accent)]"
                  />
                  Include free gift lines
                </label>
              </>
            )}
          </Card>

          <Card title="Saved views">
            <ul className="space-y-1.5 text-sm">
              {SAVED_VIEWS.map((v) => {
                const active =
                  v.tab === tab &&
                  v.period === period &&
                  (v.tab === 'general'
                    ? true
                    : v.tab === 'sales'
                      ? v.group === groupBy
                      : v.productGroup === productGroupBy && (v.rank ?? 'revenue') === rank);
                return (
                  <li key={v.label}>
                    <button
                      onClick={() => applyView(v)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded transition flex items-center justify-between',
                        active
                          ? 'bg-accent-soft text-accent font-medium'
                          : 'text-ink-2 hover:bg-paper-2',
                      )}
                    >
                      <span>{v.label}</span>
                      {active && <span className="text-xs">✓</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {/* RIGHT — the active report */}
        <div className="lg:col-span-3 space-y-6">
          {tab === 'general' ? (
            <GeneralReportView
              report={generalReport.data}
              loading={generalReport.isLoading}
              onExport={exportExcel}
              exporting={exporting}
            />
          ) : tab === 'sales' ? (
            <SalesReportView report={salesReport.data} loading={salesReport.isLoading} groupBy={groupBy} />
          ) : (
            <ProductReportView
              report={productReport.data}
              loading={productReport.isLoading}
              groupBy={productGroupBy}
              rank={rank}
            />
          )}
        </div>
      </div>
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sales report (shops / salespeople / categories / payment methods)
// ─────────────────────────────────────────────────────────────────────────────
function SalesReportView({
  report,
  loading,
  groupBy,
}: {
  report: SalesReport | undefined;
  loading: boolean;
  groupBy: Group;
}): JSX.Element {
  const tableTitle =
    groupBy === 'shop_salesperson'
      ? 'Sales by shop × salesperson'
      : groupBy === 'shop'
        ? 'Sales by shop'
        : groupBy === 'salesperson'
          ? 'Sales by salesperson'
          : groupBy === 'category'
            ? 'Sales by category'
            : 'Payments by method';

  return (
    <>
      {report && (
        <Card bodyClassName="p-0">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-rule-2">
            <Metric label="Gross sales" value={<Money value={report.totals.gross} />} />
            <Metric
              label="Discounts"
              value={<Money value={report.totals.discount} className="text-negative" />}
              hint={
                report.totals.gross > 0
                  ? `${((report.totals.discount / report.totals.gross) * 100).toFixed(1)}% of gross`
                  : undefined
              }
            />
            <Metric label="VAT" value={<Money value={report.totals.vat} />} hint="15% standard" />
            <Metric
              label="Net sales"
              value={<Money value={report.totals.net} className="text-accent" />}
              hint={`${report.totals.orderCount} orders`}
            />
          </div>
        </Card>
      )}

      {report && report.rows.length > 0 && (
        <Card
          title={`Sales (incl. VAT) · ${report.period.label}`}
          subtitle={`Per ${report.trend.unit} · aggregated across all rows in the current view`}
        >
          <TrendChart
            points={aggregateSparkline(report.rows)}
            labels={report.trend.labels}
            accent="var(--accent)"
          />
        </Card>
      )}

      {report && report.rows.length > 0 && (
        <Card
          title={
            groupBy === 'shop_salesperson'
              ? 'Sales (incl. VAT) by shop × salesperson'
              : groupBy === 'shop'
                ? 'Sales (incl. VAT) by shop'
                : groupBy === 'salesperson'
                  ? 'Sales (incl. VAT) by salesperson'
                  : groupBy === 'category'
                    ? 'Sales (incl. VAT) by category'
                    : 'Payments by method'
          }
          subtitle={`Top ${Math.min(10, report.rows.length)} of ${report.rows.length}`}
        >
          <HorizontalBarChart
            rows={report.rows.slice(0, 10).map((r) => ({
              key: r.key,
              label: r.shop && r.salesperson ? `${r.shop.code} · ${r.salesperson.name}` : r.label,
              value: r.total,
            }))}
          />
        </Card>
      )}

      <Card title={tableTitle} subtitle="Sorted by total · trend spans the selected period" bodyClassName="p-0">
        {loading || !report ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : report.rows.length === 0 ? (
          <EmptyState title="No data" description="No sales match these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">{GROUPS.find((g) => g.key === groupBy)?.label}</th>
                  <th className="px-3 py-2 font-medium text-right">Orders</th>
                  <th className="px-3 py-2 font-medium text-right">Gross</th>
                  <th className="px-3 py-2 font-medium text-right">Disc.</th>
                  <th className="px-3 py-2 font-medium text-right">Net</th>
                  {groupBy !== 'method' && groupBy !== 'category' && (
                    <th className="px-3 py-2 font-medium text-right">Avg ticket</th>
                  )}
                  {(groupBy === 'shop' || groupBy === 'salesperson' || groupBy === 'shop_salesperson') && (
                    <th className="px-3 py-2 font-medium text-right">Comm.</th>
                  )}
                  <th className="px-3 py-2 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.key} className="border-b border-rule-2">
                    <td className="px-6 py-3">
                      {r.shop && r.salesperson ? (
                        <>
                          <span className="text-ink">{r.salesperson.name}</span>
                          <div className="text-xs text-ink-3 kc-mono mt-0.5">
                            {r.shop.code} · {r.shop.name}
                          </div>
                        </>
                      ) : (
                        <span className="text-ink">{r.label}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right kc-mono">{r.orderCount}</td>
                    <td className="px-3 py-3 text-right">
                      <Money value={r.gross} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      {r.discount > 0 ? (
                        <Money value={-r.discount} className="text-negative" />
                      ) : (
                        <span className="text-ink-4">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Money value={r.net} className="font-medium" />
                    </td>
                    {groupBy !== 'method' && groupBy !== 'category' && (
                      <td className="px-3 py-3 text-right text-ink-3">
                        <Money value={r.avgTicket} muted />
                      </td>
                    )}
                    {(groupBy === 'shop' || groupBy === 'salesperson' || groupBy === 'shop_salesperson') && (
                      <td className="px-3 py-3 text-right">
                        {r.commission > 0 ? (
                          <Money value={r.commission} className="text-accent" />
                        ) : (
                          <span className="text-ink-4">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <Sparkline points={r.sparkline} />
                    </td>
                  </tr>
                ))}
                <tr className="bg-paper-2 font-medium">
                  <td className="px-6 py-3 text-ink">Total</td>
                  <td className="px-3 py-3 text-right kc-mono">{report.totals.orderCount}</td>
                  <td className="px-3 py-3 text-right">
                    <Money value={report.totals.gross} />
                  </td>
                  <td className="px-3 py-3 text-right text-negative">
                    <Money value={-report.totals.discount} muted={report.totals.discount === 0} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Money value={report.totals.net} className="text-accent" />
                  </td>
                  {groupBy !== 'method' && groupBy !== 'category' && <td />}
                  {(groupBy === 'shop' || groupBy === 'salesperson' || groupBy === 'shop_salesperson') && (
                    <td className="px-3 py-3 text-right text-accent">
                      <Money value={report.totals.commission} />
                    </td>
                  )}
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Product report (best sellers by model / variant / category)
// ─────────────────────────────────────────────────────────────────────────────
function ProductReportView({
  report,
  loading,
  groupBy,
  rank,
}: {
  report: ProductReport | undefined;
  loading: boolean;
  groupBy: ProductGroup;
  rank: Rank;
}): JSX.Element {
  const nameHeader =
    groupBy === 'category' ? 'Category' : groupBy === 'product' ? 'Product (variant)' : 'Model';

  return (
    <>
      {report && (
        <Card bodyClassName="p-0">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-rule-2">
            <Metric
              label="Units sold"
              value={<span className="kc-mono">{report.totals.qty.toLocaleString()}</span>}
              hint={report.totals.returnedQty > 0 ? `${report.totals.returnedQty} returned` : undefined}
            />
            <Metric
              label="Revenue (incl. VAT)"
              value={<Money value={report.totals.total} className="text-accent" />}
              hint={`net ${Math.round(report.totals.net).toLocaleString()} + VAT`}
            />
            <Metric
              label="Orders"
              value={<span className="kc-mono">{report.totals.orderCount.toLocaleString()}</span>}
              hint={`${report.totals.productCount} distinct ${groupBy === 'category' ? 'categories' : groupBy === 'product' ? 'variants' : 'models'}`}
            />
            <Metric
              label="Avg unit price"
              value={<Money value={report.totals.qty > 0 ? report.totals.total / report.totals.qty : 0} />}
              hint="incl. VAT"
            />
          </div>
        </Card>
      )}

      {report && report.rows.length > 0 && (
        <Card
          title={`Best sellers by ${rank === 'qty' ? 'units sold' : 'revenue (incl. VAT)'}`}
          subtitle={`Top ${Math.min(10, report.rows.length)} of ${report.rows.length} · ${report.period.label}`}
        >
          <HorizontalBarChart
            rows={report.rows.slice(0, 10).map((r) => ({
              key: r.key,
              label: r.label,
              value: rank === 'qty' ? r.qty : r.total,
              money: rank !== 'qty',
            }))}
          />
        </Card>
      )}

      <Card
        title={`Product performance · by ${PRODUCT_GROUPS.find((g) => g.key === groupBy)?.label.toLowerCase()}`}
        subtitle={`Ranked by ${rank === 'qty' ? 'units sold' : 'revenue'} · trend spans the selected period`}
        bodyClassName="p-0"
      >
        {loading || !report ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : report.rows.length === 0 ? (
          <EmptyState title="No data" description="No product sales match these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="pl-6 pr-2 py-2 font-medium text-right w-10">#</th>
                  <th className="px-3 py-2 font-medium">{nameHeader}</th>
                  <th className="px-3 py-2 font-medium text-right">Units</th>
                  <th className="px-3 py-2 font-medium text-right">Ret.</th>
                  <th className="px-3 py-2 font-medium text-right">Orders</th>
                  <th className="px-3 py-2 font-medium text-right">Net</th>
                  <th className="px-3 py-2 font-medium text-right">Total</th>
                  <th className="px-3 py-2 font-medium text-right">Share</th>
                  <th className="px-3 py-2 font-medium text-right">Avg unit</th>
                  <th className="px-3 py-2 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => (
                  <tr key={r.key} className="border-b border-rule-2">
                    <td className="pl-6 pr-2 py-3 text-right kc-mono text-ink-3">{i + 1}</td>
                    <td className="px-3 py-3">
                      <span className="text-ink">{r.label}</span>
                      {(r.secondary || r.category) && (
                        <div className="text-xs text-ink-3 mt-0.5">
                          {r.secondary && <span className="kc-mono">{r.secondary}</span>}
                          {r.secondary && r.category && ' · '}
                          {r.category}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right kc-mono font-medium">{r.qty}</td>
                    <td className="px-3 py-3 text-right kc-mono">
                      {r.returnedQty > 0 ? (
                        <span className="text-negative">{r.returnedQty}</span>
                      ) : (
                        <span className="text-ink-4">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right kc-mono">{r.orderCount}</td>
                    <td className="px-3 py-3 text-right">
                      <Money value={r.net} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Money value={r.total} className="font-medium" />
                    </td>
                    <td className="px-3 py-3 text-right kc-mono text-ink-3">{PCT.format(r.share)}</td>
                    <td className="px-3 py-3 text-right text-ink-3">
                      <Money value={r.avgPrice} muted />
                    </td>
                    <td className="px-3 py-3">
                      <Sparkline points={r.sparkline} />
                    </td>
                  </tr>
                ))}
                <tr className="bg-paper-2 font-medium">
                  <td />
                  <td className="px-3 py-3 text-ink">Total</td>
                  <td className="px-3 py-3 text-right kc-mono">{report.totals.qty}</td>
                  <td className="px-3 py-3 text-right kc-mono">
                    {report.totals.returnedQty > 0 ? report.totals.returnedQty : '—'}
                  </td>
                  <td className="px-3 py-3 text-right kc-mono">{report.totals.orderCount}</td>
                  <td className="px-3 py-3 text-right">
                    <Money value={report.totals.net} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Money value={report.totals.total} className="text-accent" />
                  </td>
                  <td className="px-3 py-3 text-right kc-mono">100%</td>
                  <td />
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// General report — the director's per-sale template, previewed before download
// ─────────────────────────────────────────────────────────────────────────────
// The page shows what the sheet will contain and hands over the .xlsx; it is not
// meant to be read 8,000 rows deep in a browser. Columns come from the backend
// (general-report.ts owns the template) so the preview can't drift from the file.
const GENERAL_DATE_KEYS = new Set(['date', 'expectedDelivery', 'actualDelivery']);
const GENERAL_MONEY_KEYS = new Set([
  'commission',
  'discount',
  'amount',
  'cash',
  'cheque',
  'card',
  'juice',
  'bankTransfer',
  'cim',
  'rogersCapital',
  'otherTender',
  'balanceDue',
]);
const MUR2 = new Intl.NumberFormat('en-MU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function generalCell(key: string, value: string | number | null): string {
  if (value == null || value === '') return '';
  if (GENERAL_DATE_KEYS.has(key)) {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime())
      ? String(value)
      : `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
  }
  if (GENERAL_MONEY_KEYS.has(key)) return MUR2.format(Number(value));
  return String(value);
}

function GeneralReportView({
  report,
  loading,
  onExport,
  exporting,
}: {
  report: GeneralReport | undefined;
  loading: boolean;
  onExport: () => Promise<void>;
  exporting: boolean;
}): JSX.Element {
  return (
    <>
      {report && (
        <Card bodyClassName="p-0">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-rule-2">
            <Metric
              label="Sales"
              value={<span className="kc-mono">{report.totals.orders.toLocaleString()}</span>}
              hint={`${report.totals.rows.toLocaleString()} sheet rows · ${report.totals.qty.toLocaleString()} items`}
            />
            <Metric
              label="Amount (incl. VAT)"
              value={<Money value={report.totals.amount} className="text-accent" />}
              hint={
                report.totals.discount > 0
                  ? `after ${Math.round(report.totals.discount).toLocaleString()} discount`
                  : undefined
              }
            />
            <Metric label="Collected" value={<Money value={report.totals.collected} />} hint="all tenders" />
            <Metric
              label="Balance due"
              value={
                <Money
                  value={report.totals.balanceDue}
                  className={report.totals.balanceDue > 0 ? 'text-negative' : undefined}
                />
              }
              hint={`commission ${Math.round(report.totals.commission).toLocaleString()}`}
            />
          </div>
        </Card>
      )}

      <Card
        title="General report"
        subtitle={
          report
            ? `${report.rowCount.toLocaleString()} row${report.rowCount === 1 ? '' : 's'} · ${report.period.label} · ${report.columns.length} columns${report.truncated ? ` · previewing the first ${report.rows.length}` : ''}`
            : 'One row per sale — mattress, bed base and small items side by side'
        }
        bodyClassName="p-0"
      >
        <div className="px-6 py-4 border-b border-rule-2 flex items-start justify-between gap-4">
          <p className="text-sm text-ink-3 max-w-2xl">
            Downloads as an Excel sheet in the director's template, with a filter dropdown on every
            column heading. Money columns (Amount, Discount, Commission, each tender, Balance due) are
            written once per sale — on its first row — so any column still totals correctly. Leave the
            filters alone for the whole book; set a period, a shop or a salesperson to narrow it.
          </p>
          <Button onClick={() => void onExport()} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Download Excel'}
          </Button>
        </div>

        {loading || !report ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : report.rows.length === 0 ? (
          <EmptyState title="No sales" description="No sales match these filters." />
        ) : (
          <div className="overflow-auto max-h-[70vh]">
            <table className="text-xs whitespace-nowrap">
              <thead className="sticky top-0 z-10">
                <tr className="text-left uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  {report.columns.map((c) => (
                    <th
                      key={c.key}
                      className={cn(
                        'px-3 py-2 font-medium',
                        GENERAL_MONEY_KEYS.has(c.key) && 'text-right',
                      )}
                    >
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => (
                  <tr key={`${String(r.orderNo)}-${i}`} className="border-b border-rule-2">
                    {report.columns.map((c) => (
                      <td
                        key={c.key}
                        className={cn(
                          'px-3 py-2',
                          GENERAL_MONEY_KEYS.has(c.key) ? 'text-right kc-mono' : 'text-ink-2',
                        )}
                      >
                        {generalCell(c.key, r[c.key] ?? null)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────────────
function FilterChips<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ key: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold mb-2">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={cn(
              'px-3 py-1.5 rounded text-xs transition border',
              value === o.key
                ? 'bg-accent text-white border-accent'
                : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="kc-input text-sm">
        {children}
      </select>
    </label>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}): JSX.Element {
  return (
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
      <div className="text-2xl text-ink mt-1 font-medium kc-serif">{value}</div>
      {hint && <div className="text-xs text-ink-3 mt-1">{hint}</div>}
    </div>
  );
}

function Sparkline({ points }: { points: number[] }): JSX.Element {
  const w = 64;
  const h = 18;
  const max = Math.max(...points, 1);
  const min = 0;
  const range = max - min || 1;
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${(i / Math.max(points.length - 1, 1)) * w},${h - ((p - min) / range) * h}`,
    )
    .join(' ');
  const empty = points.every((p) => p === 0);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block">
      {empty ? (
        <line x1="0" x2={w} y1={h - 1} y2={h - 1} stroke="var(--rule)" strokeWidth="1" />
      ) : (
        <path
          d={path}
          stroke="var(--accent)"
          strokeWidth={1.4}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

// Sum row sparklines into a single per-bucket series. Every row's sparkline is
// aligned to the same trend buckets, so element-wise sum is correct.
function aggregateSparkline(rows: Array<{ sparkline: number[] }>): number[] {
  if (rows.length === 0) return [];
  const len = rows[0]?.sparkline.length ?? 0;
  const out = Array.from({ length: len }, () => 0);
  for (const r of rows) {
    for (let i = 0; i < len; i++) out[i]! += r.sparkline[i] ?? 0;
  }
  return out;
}

const KCD = new Intl.NumberFormat('en-MU', { maximumFractionDigits: 0 });

function TrendChart({
  points,
  labels,
  accent,
}: {
  points: number[];
  labels: string[];
  accent: string;
}): JSX.Element {
  const w = 800;
  const h = 200;
  const padL = 56;
  const padR = 16;
  const padT = 16;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const max = Math.max(...points, 1);
  const xStep = innerW / Math.max(points.length - 1, 1);
  const xy = points.map((p, i) => ({ x: padL + i * xStep, y: padT + innerH - (p / max) * innerH }));
  const linePath = xy
    .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)},${pt.y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${xy[xy.length - 1]?.x.toFixed(1) ?? 0},${padT + innerH} L ${xy[0]?.x.toFixed(1) ?? 0},${padT + innerH} Z`;
  const gridLines = 4;
  // Single-bucket ranges (e.g. "Today") draw as a centered dot, not a line.
  const single = points.length === 1;

  const labelIdxs =
    points.length <= 1 ? [0] : [...new Set([0, Math.floor(points.length / 2), points.length - 1])];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto block" preserveAspectRatio="none">
      <defs>
        <linearGradient id="trend-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.18" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Y-axis gridlines + labels */}
      {Array.from({ length: gridLines + 1 }).map((_, i) => {
        const y = padT + (innerH / gridLines) * i;
        const v = max - (max / gridLines) * i;
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="var(--rule-2)" strokeWidth={1} />
            <text x={padL - 8} y={y + 4} textAnchor="end" fontSize={10} fill="var(--ink-3)">
              {KCD.format(v)}
            </text>
          </g>
        );
      })}

      {/* X-axis labels — first, middle, last bucket */}
      {labelIdxs.map((i) => {
        const x = padL + i * xStep;
        return (
          <text key={i} x={x} y={h - 8} textAnchor="middle" fontSize={10} fill="var(--ink-3)">
            {labels[i] ?? ''}
          </text>
        );
      })}

      {points.every((p) => p === 0) ? (
        <text x={w / 2} y={h / 2} textAnchor="middle" fontSize={12} fill="var(--ink-3)">
          No data
        </text>
      ) : single ? (
        <circle cx={xy[0]!.x} cy={xy[0]!.y} r={4} fill={accent} />
      ) : (
        <>
          <path d={areaPath} fill="url(#trend-fill)" />
          <path
            d={linePath}
            stroke={accent}
            strokeWidth={1.8}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {xy.map((pt, i) => (
            <circle key={i} cx={pt.x} cy={pt.y} r={2} fill={accent} />
          ))}
        </>
      )}
    </svg>
  );
}

function HorizontalBarChart({
  rows,
}: {
  rows: Array<{ key: string; label: string; value: number; money?: boolean }>;
}): JSX.Element {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.key} className="text-sm">
            <div className="flex items-baseline justify-between mb-1 gap-3">
              <span className="text-ink truncate">{r.label}</span>
              {r.money === false ? (
                <span className="text-xs kc-mono shrink-0">{r.value.toLocaleString()} units</span>
              ) : (
                <Money value={r.value} className="text-xs kc-mono shrink-0" />
              )}
            </div>
            <div className="h-2.5 bg-rule-2 rounded overflow-hidden">
              <div
                className={i === 0 ? 'h-full bg-accent rounded' : 'h-full bg-ink-2 rounded'}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
